import { lazy, Suspense } from 'react';
import { SignedIn, SignedOut, RedirectToSignIn } from '@clerk/clerk-react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useSocketInit } from './hooks/useSocket';
import AppShell from './components/layout/AppShell';
import TosAcceptanceGate from './components/legal/TosAcceptanceGate';
import SignInPage from './pages/auth/SignInPage';
import SignUpPage from './pages/auth/SignUpPage';
import Spinner from './components/ui/Spinner';

// Lazy-load pages for code splitting
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const ConversationsPage = lazy(() => import('./pages/conversations/ConversationsPage'));
const ConversationDetailPage = lazy(() => import('./pages/conversations/ConversationDetailPage'));
const TicketsPage = lazy(() => import('./pages/tickets/TicketsPage'));
const TicketDetailPage = lazy(() => import('./pages/tickets/TicketDetailPage'));
const CustomersPage = lazy(() => import('./pages/customers/CustomersPage'));
const CustomerDetailPage = lazy(() => import('./pages/customers/CustomerDetailPage'));
const AnalyticsPage = lazy(() => import('./pages/analytics/AnalyticsPage'));
const KnowledgeBasePage = lazy(() => import('./pages/knowledge-base/KnowledgeBasePage'));
const KnowledgeBaseDocPage = lazy(() => import('./pages/knowledge-base/KnowledgeBaseDocPage'));
const FlowsPage = lazy(() => import('./pages/flows/FlowsPage'));
const FlowBuilderPage = lazy(() => import('./pages/flows/FlowBuilderPage'));
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'));
const TeamChatPage = lazy(() => import('./pages/team-chat/TeamChatPage'));
const CoachingPage = lazy(() => import('./pages/coaching/CoachingPage'));
const QualityReviewPage = lazy(() => import('./pages/quality/QualityReviewPage'));
const MarketplacePage = lazy(() => import('./pages/marketplace/MarketplacePage'));
const ResellerDashboardPage = lazy(() => import('./pages/reseller/ResellerDashboardPage'));
const AdminPage = lazy(() => import('./pages/admin/AdminPage'));
const WorkloadPage = lazy(() => import('./pages/workload/WorkloadPage'));
const MonitorPage = lazy(() => import('./pages/monitor/MonitorPage'));
const LeadsPage = lazy(() => import('./pages/leads/LeadsPage'));
const OnboardingWizard = lazy(() => import('./pages/onboarding/OnboardingWizard'));

function PageLoader() {
  return (
    <div className="h-full flex items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
}

function AuthenticatedApp() {
  useSocketInit();
  return (
    <>
      {/* Blocking ToS clickwrap — overlays everything until the current
          TOS_VERSION is accepted (clickwrap acceptance logging). */}
      <TosAcceptanceGate />
      <AppShell>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/conversations" element={<ConversationsPage />} />
          <Route path="/conversations/:id" element={<ConversationDetailPage />} />
          <Route path="/tickets" element={<TicketsPage />} />
          <Route path="/tickets/:id" element={<TicketDetailPage />} />
          <Route path="/customers" element={<CustomersPage />} />
          <Route path="/customers/:id" element={<CustomerDetailPage />} />
          <Route path="/leads" element={<LeadsPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/knowledge-base" element={<KnowledgeBasePage />} />
          <Route path="/knowledge-base/:id" element={<KnowledgeBaseDocPage />} />
          <Route path="/flows" element={<FlowsPage />} />
          <Route path="/flows/:id" element={<FlowBuilderPage />} />
          <Route path="/team-chat" element={<TeamChatPage />} />
          <Route path="/coaching" element={<CoachingPage />} />
          <Route path="/quality" element={<QualityReviewPage />} />
          <Route path="/marketplace" element={<MarketplacePage />} />
          <Route path="/reseller" element={<ResellerDashboardPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/workload" element={<WorkloadPage />} />
          <Route path="/monitor" element={<MonitorPage />} />
          <Route path="/settings/*" element={<SettingsPage />} />
          <Route path="/onboarding" element={<OnboardingWizard />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
      </AppShell>
    </>
  );
}

export default function App() {
  return (
    <>
      <SignedIn>
        <Routes>
          <Route path="/*" element={<AuthenticatedApp />} />
        </Routes>
      </SignedIn>
      <SignedOut>
        <Routes>
          <Route path="/sign-in" element={<SignInPage />} />
          <Route path="/sign-up" element={<SignUpPage />} />
          <Route path="*" element={<RedirectToSignIn />} />
        </Routes>
      </SignedOut>
    </>
  );
}
